/**
 * 认证接口（路径与字段严格对齐 apps/api/src/modules/auth/auth.routes.ts）。
 */
import type { AccountRole } from '@fht/shared';
import {
  clearSession,
  api,
  refreshSession,
  request,
  setAccessToken,
  AUTH_ENDPOINTS,
} from './client';
import type {
  CaptchaResponse,
  LoginResponse,
  MeResponse,
  RegisterResponse,
  SessionListResponse,
  SessionResponse,
} from './types';

export interface LoginPayload {
  /**
   * 登录角色。`system_admin` 走独立的 /admin/login 页面提交（见方案 2B.2）：
   * 管理入口的设计意图是不对公网暴露（Caddy 限制内网/隧道来源，**线上尚未生效**，
   * 见 docs/RUNBOOK.md §5.4），登录页也不放在家长/学生的导航里。
   */
  role: AccountRole;
  username: string;
  password: string;
  rememberDevice: boolean;
  /** 连续失败次数；后端达到阈值（CAPTCHA_AFTER_FAILURES，默认 1）后强制要验证码。 */
  captchaFailures: number;
  captchaId?: string;
  captchaAnswer?: string;
  totpCode?: string;
}

export async function login(payload: LoginPayload): Promise<LoginResponse> {
  const data = await api.post<LoginResponse>(AUTH_ENDPOINTS.login, payload, { auth: false });
  setAccessToken(data.accessToken);
  return data;
}

/** 登出：先通知后端吊销 Refresh 会话，再清内存；后端失败也要清本地。 */
export async function logout(): Promise<void> {
  try {
    await request<{ loggedOut: boolean }>(AUTH_ENDPOINTS.logout, {
      method: 'POST',
      auth: false,
      skipRefresh: true,
    });
  } finally {
    clearSession();
  }
}

export async function me(): Promise<MeResponse> {
  return api.get<MeResponse>(AUTH_ENDPOINTS.me);
}

export async function fetchCaptcha(): Promise<CaptchaResponse> {
  return api.get<CaptchaResponse>(AUTH_ENDPOINTS.captcha, undefined, { auth: false });
}

export interface RegisterPayload {
  nickname: string;
  username: string;
  email: string;
  password: string;
  relationLabel: string;
  familyName?: string;
}

export async function registerParent(payload: RegisterPayload): Promise<RegisterResponse> {
  return api.post<RegisterResponse>('/auth/parent/register', payload, { auth: false });
}

export async function confirmRegisterEmail(email: string, code: string): Promise<void> {
  await api.post('/auth/parent/register/confirm', { email, code }, { auth: false });
}

export async function resendRegisterEmail(email: string): Promise<void> {
  await api.post('/auth/parent/register/resend', { email }, { auth: false });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await api.post('/auth/password/reset/request', { email }, { auth: false });
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await api.post('/auth/password/reset/confirm', { email, code, newPassword }, { auth: false });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/auth/password/change', { currentPassword, newPassword });
}

export async function listDevices(): Promise<SessionListResponse> {
  return api.get<SessionListResponse>('/auth/sessions');
}

export async function revokeDevice(id: string): Promise<void> {
  await api.del(`/auth/sessions/${id}`);
}

/**
 * 批量移除"其它全部设备"（**当前设备除外**）。
 *
 * 当前设备由**服务端**从 Refresh Cookie 认出来，前端传什么都改变不了这一点：
 * 认不出来时后端返回 409（CURRENT_SESSION_UNKNOWN）并且**一条都不删** ——
 * 宁可让用户重试，也不能把正在用的设备一起吊销、把人踢下线。
 */
export async function revokeOtherDevices(): Promise<{ revoked: number }> {
  return api.post<{ revoked: number }>('/auth/sessions/revoke-others');
}

/**
 * 清理本账号的历史失效会话（已吊销 / 已过期），返回被删条数。
 * 保留策略见 `@fht/shared` 的 `SESSION_RETENTION`（活会话一条不删）。
 */
export async function cleanupSessions(): Promise<{ purged: number }> {
  return api.post<{ purged: number }>('/auth/sessions/cleanup');
}

/**
 * 应用启动时静默恢复会话：refresh 成功后 GET /auth/me。
 * 返回 null 表示"没有可恢复的会话"，属于正常情况（未登录），不抛错。
 */
export async function restoreSession(): Promise<MeResponse | null> {
  const session: SessionResponse | null = await refreshSession();
  if (!session) return null;
  try {
    return await me();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- 失败计数（localStorage）

/**
 * 图形验证码的"该不该显示"由前端保存的失败次数决定（见方案 5.4）：
 * 失败次数存在本机 localStorage，登录时回传 `captchaFailures`，后端据此强制要求。
 * 这里存的是**登录失败次数**，不是敏感数据，放 localStorage 是刻意的
 * （换标签页/刷新后仍要记得，否则用户能靠刷新绕过验证码）。
 */
const FAILURE_KEY = 'fht.login.failures';

type FailureMap = Record<string, number>;

function failureKeyOf(role: string, username: string): string {
  return `${role}:${username.trim().toLowerCase()}`;
}

function readFailures(): FailureMap {
  try {
    const raw = globalThis.localStorage?.getItem(FAILURE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as FailureMap;
  } catch {
    return {};
  }
}

function writeFailures(map: FailureMap): void {
  try {
    globalThis.localStorage?.setItem(FAILURE_KEY, JSON.stringify(map));
  } catch {
    // 隐私模式下 localStorage 可能不可写：忽略，验证码退化为"本次会话内记住"
  }
}

let memoryFallback: FailureMap = {};

export function getLoginFailures(role: string, username: string): number {
  const key = failureKeyOf(role, username);
  return readFailures()[key] ?? memoryFallback[key] ?? 0;
}

export function recordLoginFailure(role: string, username: string): number {
  const key = failureKeyOf(role, username);
  const map = readFailures();
  const next = (map[key] ?? memoryFallback[key] ?? 0) + 1;
  map[key] = next;
  memoryFallback = map;
  writeFailures(map);
  return next;
}

export function clearLoginFailures(role: string, username: string): void {
  const key = failureKeyOf(role, username);
  const map = readFailures();
  delete map[key];
  delete memoryFallback[key];
  writeFailures(map);
}
